import { usePermissionRouteConfig } from 'components/Router/context';
import { matchPath } from 'react-router-dom';
import { RouteConfig } from 'types';
import { scenarioTypeI18nLabelMap } from 'views/Aide/Scenario/config';

export const getPathList = (path: string) => path.split('/').filter((_) => _);

export const getRouteList = (routes: RouteConfig[], path: string) => {
  const matchPathList = (pathList: string[], targetList: string[]) => {
    if (
      targetList.every(
        (path, index) =>
          path === pathList[index] ||
          (path.startsWith(':') && pathList[index]) ||
          (path.endsWith('?') && !pathList[index]),
      )
    ) {
      return pathList.slice(targetList.length);
    }
  };

  const getRouteList = (
    routes: RouteConfig[],
    pathList: string[],
    result: RouteConfig[] = [],
  ): RouteConfig[] => {
    for (const route of routes) {
      const { path, children = [] } = route;

      const restPath = matchPathList(pathList, getPathList(path));

      if (restPath) {
        result.push(route);

        if (restPath.length === 0 && children.length === 0) {
          return result;
        }

        return getRouteList(children, restPath, result);
      }
    }

    return result;
  };

  return getRouteList(routes, getPathList(path));
};

export const useGetTitleI18nList = () => {
  const { mainRoutes } = usePermissionRouteConfig();

  return function getTitleI18nList(
    path: string,
    aiEnable?: boolean,
  ): (string | [string, string])[] {
    const pathMap: Partial<Record<string, (string | [string, string])[]>> = {
      '/knowledge/marketing': ['AI运营-菜单名称', 'AI促销机器人'],
      '/knowledge/general': ['AI运营-菜单名称', aiEnable ? '店铺知识' : '物流管理-菜单名称'],
      '/knowledge/product': ['AI运营-菜单名称', '商品知识-菜单名称'],
      '/knowledge/activityDashboard': ['活动效果'],
      '/knowledge/boost': ['增强型机器人'],
    };

    const { [path]: listFromMap } = pathMap;

    if (listFromMap) {
      const last = listFromMap[listFromMap.length - 1];
      return [last];
    }

    if (path.startsWith('/knowledge/')) {
      return [aiEnable ? '店铺知识' : '物流管理-菜单名称'];
    }

    const { params: { key: scenarioForbiddenI18nKey } = {} as any } =
      matchPath('/aideForbidden/:key', path) || {};

    if (scenarioForbiddenI18nKey) {
      return [scenarioForbiddenI18nKey];
    }

    const routeList = getRouteList(mainRoutes ?? [], path);

    const allListFromHelmet = routeList
      .map(({ i18nHelmetTitle }) => i18nHelmetTitle)
      .filter((_) => _);

    const { params: { scenarioType = '', action = '' } = {} as any } =
      matchPath(
        '/aide/scenario/policy/:scenarioType/:platform/:region/:round/:replyOption/:action/:id?',
        path,
      ) ||
      matchPath('/aide/outstock/follow/task/:platform/:action/:id?', path) ||
      matchPath('/aide/outstock/remind/task/:platform/:action/:id?', path) ||
      matchPath('/aide/phrase/:platform/:action/:id?', path) ||
      matchPath('/aide/remind/task/:action/:id?', path) ||
      matchPath('/aide/pause/task/:action/:id?', path) ||
      matchPath('/aide/todo/taskPolicy/:action/:id?', path) ||
      matchPath('/aide/task/:scenarioType/:platform/:region/:action/:id?', path) ||
      matchPath('/aide/whatsapp/template/:action/:name?', path) ||
      {};

    const last = allListFromHelmet[allListFromHelmet.length - 1];
    const listFromHelmet = scenarioType
      ? [scenarioTypeI18nLabelMap[scenarioType as keyof typeof scenarioTypeI18nLabelMap]]
      : last
      ? [last]
      : [];

    if (action) {
      const actionMap = {
        copy: 'marketing.复制',
        update: 'marketing.编辑',
        edit: 'marketing.编辑',
        create: 'marketing.新增',
        view: 'aide.查看',
      };

      listFromHelmet.unshift(actionMap[action as keyof typeof actionMap]);
    }

    return listFromHelmet.filter((_) => _);
  };
};
